import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Flame, Mail, Lock, User, ArrowRight, ShieldCheck } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../context/AuthContext';
import { HERO_IMAGES } from '../mock';

export default function Register() {
  const { register } = useAuth();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: '', email: '', password: '' });
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (form.password.length < 6) {
      toast({ title: 'Password too short', description: 'Use at least 6 characters.', variant: 'destructive' });
      return;
    }
    setLoading(true);
    try {
      const u = await register(form.name, form.email, form.password);
      toast({ title: `Welcome, ${u.name.split(' ')[0]}!`, description: 'Your account has been created.' });
      navigate('/', { replace: true });
    } catch (err) {
      toast({ title: 'Registration failed', description: err.message, variant: 'destructive' });
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-[#070a12]">
      <div className="flex items-center justify-center p-8 order-2 lg:order-1">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="w-full max-w-md">
          <Link to="/" className="lg:hidden flex items-center gap-2.5 mb-8">
            <span className="grid place-items-center w-10 h-10 rounded-xl bg-gradient-to-br from-primary to-accent text-white"><Flame className="w-5 h-5" /></span>
            <span className="font-display text-2xl font-extrabold tracking-wide text-white">CLOSE<span className="fire-text">FIRE</span></span>
          </Link>
          <h1 className="font-heading font-extrabold text-4xl text-white">Create Account</h1>
          <p className="text-gray-400 mt-2">Join Closefire to manage enquiries and services.</p>

          <form onSubmit={submit} className="mt-8 space-y-5">
            <div>
              <Label htmlFor="name" className="text-gray-300">Full name</Label>
              <div className="relative mt-1.5">
                <User className="w-4 h-4 text-gray-500 absolute left-3 top-1/2 -translate-y-1/2" />
                <Input id="name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="John Doe" className="h-12 pl-10 bg-white/5 border-white/10 text-white placeholder:text-gray-500" />
              </div>
            </div>
            <div>
              <Label htmlFor="email" className="text-gray-300">Email</Label>
              <div className="relative mt-1.5">
                <Mail className="w-4 h-4 text-gray-500 absolute left-3 top-1/2 -translate-y-1/2" />
                <Input id="email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} placeholder="you@company.com" className="h-12 pl-10 bg-white/5 border-white/10 text-white placeholder:text-gray-500" />
              </div>
            </div>
            <div>
              <Label htmlFor="password" className="text-gray-300">Password</Label>
              <div className="relative mt-1.5">
                <Lock className="w-4 h-4 text-gray-500 absolute left-3 top-1/2 -translate-y-1/2" />
                <Input id="password" type="password" required value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder="At least 6 characters" className="h-12 pl-10 bg-white/5 border-white/10 text-white placeholder:text-gray-500" />
              </div>
            </div>
            <Button type="submit" disabled={loading} size="lg" className="w-full gap-2 h-12 bg-gradient-to-r from-primary to-accent hover:opacity-90">
              {loading ? 'Creating…' : <>Create Account <ArrowRight className="w-4 h-4" /></>}
            </Button>
          </form>

          <p className="text-center text-gray-400 mt-6">Already have an account? <Link to="/login" className="text-primary font-semibold hover:underline">Sign in</Link></p>
        </motion.div>
      </div>

      <div className="relative hidden lg:block order-1 lg:order-2 overflow-hidden">
        <img src={HERO_IMAGES[1]} alt="Fire safety" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 hero-vignette" />
        <div className="orb w-[400px] h-[400px] bg-accent/25 bottom-10 -right-20 animate-glow" />
        <div className="relative h-full flex flex-col justify-end p-12 text-white">
          <div className="section-eyebrow fire-text"><ShieldCheck className="w-4 h-4 text-primary" /> Join Closefire</div>
          <h2 className="font-heading font-extrabold text-4xl mt-4 max-w-md">Your partner in fire prevention and protection.</h2>
          <p className="text-gray-300 mt-4 max-w-md">Create an account to track enquiries, services and fire-safety schedules.</p>
        </div>
      </div>
    </div>
  );
}
